import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isArray, isEmpty } from 'lodash';
import { withTranslation } from 'react-i18next';
import './Input.scss';

class Input extends Component {
  constructor(props) {
    super(props);

    this.handleChange = this.handleChange.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleAddonClick = this.handleAddonClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  handleChange(e) {
    if (
      this.props.type === 'number' &&
      ((this.props.maxLength && this.props.maxLength < e.target.value.length) ||
        (!this.props.allowNegativeValue && e.target.value < 0))
    ) {
      return false;
    }

    if (this.props.onChange) {
      this.props.onChange(e);
    }
  }

  handleFocus(e) {
    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
  }

  handleBlur(e) {
    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
  }

  handleAddonClick(e) {
    if (this.props.onAddonAction) {
      this.props.onAddonAction(e);
    }
  }

  handleKeyDown(e) {
    if (
      this.props.type === 'number' &&
      (e.keyCode < 8 || e.keyCode > 57) &&
      (e.keyCode < 96 || e.keyCode > 105)
    ) {
      e.preventDefault();
      return false;
    }
  }

  render() {
    const {
      maxLength,
      type,
      showMaxLength,
      value,
      infoText,
      isValueChanged,
      clearValue,
      addon,
      size,
      labelInfo,
    } = this.props;
    let { errors } = this.props;
    const divClasses = [
      'input-container',
      'form-group',
      size,
      this.props.disabled ? 'disabled' : '',
      this.props.containerClassName,
    ];
    const inputClasses = [this.props.className];
    errors = errors && !isArray(errors) ? [errors] : errors;
    if (errors && errors.length) {
      divClasses.push('error-input');
    }
    const labelClassName =
      type === 'text' || type === 'email' || type === 'number' ? 'label-input' : '';

    return (
      <div className={classnames(divClasses)}>
        {this.props.label && (
          <label
            htmlFor={this.props.id || this.props.name}
            className={`${errors && errors.length && labelClassName ? 'label-error' : ''} ${
              isValueChanged ? 'value-changed' : ''
            } ${labelClassName} w-10`}>
            {this.props.t(this.props.label)}
            {labelInfo && <span className="fw-normal"> ({labelInfo})</span>}
          </label>
        )}
        <div
          className={`input-group ${addon && 'input-group-with-addon'} ${
            clearValue && !isEmpty(value) && 'group-btn-inside'
          }`}>
          <input
            className={classnames(inputClasses)}
            onChange={this.handleChange}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            checked={this.props.checked || this.props.value === true}
            aria-label={this.props.placeholder}
            aria-required={this.props.required}
            type={this.props.type}
            value={value}
            name={this.props.name}
            id={this.props.id || this.props.name}
            placeholder={this.props.placeholder}
            disabled={this.props.disabled}
            readOnly={this.props.readOnly}
            required={this.props.required}
            maxLength={maxLength}
            min={this.props.min}
            onKeyDown={this.handleKeyDown}
            onKeyPress={this.props.onKeyPress}
            autoComplete={this.props.autocomplete ? 'on' : 'off'}
          />
          {addon && (
            <span
              className="input-group-addon"
              onClick={this.handleAddonClick}
              onKeyDown={(e) => e.keyCode === 13 && this.handleAddonClick(e)}
              role="button"
              tabIndex="0"
              dangerouslySetInnerHTML={{ __html: addon }}
            />
          )}
          {clearValue && !isEmpty(value) && (
            <button className="btn-link btn-inside" onClick={clearValue}>
              {this.props.t('common.labels.clear')}
            </button>
          )}

          {this.props.type === 'checkbox' && this.props.children}
        </div>
        {!!maxLength && showMaxLength && (
          <span className="text-end float-end text-small">
            {this.props.t('testCenter.dateMessages.lengthLabel', {
              current: value.length,
              max: maxLength,
            })}
          </span>
        )}
        {infoText && (!errors || !errors.length || (errors.length && !errors[0])) && (
          <span className="info-text">{infoText}</span>
        )}
        {errors &&
          errors.map(
            (error, index) =>
              error && (
                <span className="text-error font-size-12" key={index}>
                  {this.props.t(error)}
                </span>
              )
          )}
        {this.props.type !== 'checkbox' && this.props.children}
      </div>
    );
  }
}

Input.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  id: PropTypes.string,
  type: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  checked: PropTypes.bool,
  placeholder: PropTypes.string,
  addon: PropTypes.string,
  label: PropTypes.string,
  size: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onKeyPress: PropTypes.func,
  onBlur: PropTypes.func,
  onAddonAction: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node,
  containerClassName: PropTypes.string,
  clearValue: PropTypes.func,
  isValueChanged: PropTypes.bool,
  errors: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  labelInfo: PropTypes.string,
  autocomplete: PropTypes.bool,
  allowNegativeValue: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

Input.defaultProps = {
  disabled: false,
  readOnly: false,
  required: false,
  checked: false,
  isValueChanged: false,
  className: '',
  value: '',
  placeholder: '',
  id: '',
  onFocus: null,
  onBlur: null,
  onKeyPress: null,
  onAddonAction: null,
  children: null,
  addon: null,
  clearValue: null,
  size: '',
  label: '',
  errors: [],
  containerClassName: '',
  labelInfo: '',
  autocomplete: true,
  allowNegativeValue: false,
};

export default withTranslation()(Input);
